import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BarChart3, Download, FileText, Filter, Printer, RefreshCw,
} from 'lucide-react';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import Alert from '../components/common/Alert';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import LoadingSpinner from '../components/common/LoadingSpinner';
import Input from '../components/forms/Input';
import Select from '../components/forms/Select';
import Table from '../components/tables/Table';
import { REPORT_TYPE_LABELS, REPORT_TYPES } from '../config/reportTypes';
import { SHEET_STATUS_META } from '../config/sheetStatus';
import { VOTER_CLASSIFICATION_META } from '../config/voterClassification';
import { useAuth } from '../context/AuthContext';
import { reportService } from '../services';
import { openPrintView } from '../utils/reportExport';

const initialFilters = {
  campaignId: '', generalAdministratorId: '', coordinatorId: '', leaderId: '',
  classification: '', voterStatus: '', sheetStatus: '', fromDate: '', toDate: '',
  zone: '', sectional: '', search: '',
};
const pieTypes = new Set([
  REPORT_TYPES.VOTERS_BY_CLASSIFICATION, REPORT_TYPES.VOTERS_BY_STATUS,
  REPORT_TYPES.SHEETS_BY_STATUS, REPORT_TYPES.CALL_RESULTS,
  REPORT_TYPES.DRIVERS_BY_TYPE, REPORT_TYPES.DUPLICATES,
]);
const colors = ['#2563eb', '#14b8a6', '#f59e0b', '#ef4444', '#8b5cf6', '#64748b'];
const slug = (value) => value.toLowerCase().normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function ReportChart({ report }) {
  if (!report?.chart?.length) {
    return <p className="py-20 text-center text-sm text-content-muted">No hay datos para graficar.</p>;
  }
  if (report.type === REPORT_TYPES.DAILY_RECORDS) {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <LineChart data={report.chart}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="name" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} />
          <Tooltip /><Line type="monotone" dataKey="value" name="Registros" stroke="#2563eb" strokeWidth={3} />
        </LineChart>
      </ResponsiveContainer>
    );
  }
  if (pieTypes.has(report.type)) {
    return (
      <ResponsiveContainer width="100%" height={320}>
        <PieChart>
          <Pie data={report.chart} dataKey="value" nameKey="name" innerRadius={58} outerRadius={105} label>
            {report.chart.map((entry, index) => <Cell key={entry.name} fill={colors[index % colors.length]} />)}
          </Pie>
          <Tooltip /><Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={report.chart} margin={{ left: 4, right: 12 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" fontSize={11} interval={0} angle={-15} height={60} />
        <YAxis allowDecimals={false} fontSize={11} /><Tooltip />
        <Bar dataKey="value" name="Valor" fill="#2563eb" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ReportsPage() {
  const { user, activeCampaign } = useAuth();
  const [type, setType] = useState(REPORT_TYPES.VOTERS_BY_COORDINATOR);
  const [filters, setFilters] = useState(initialFilters);
  const [options, setOptions] = useState({
    campaigns: [], generalAdministrators: [], coordinators: [], leaders: [],
    sectionals: [],
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await reportService.getReport(type, user, filters);
      setReport(response.data);
    } catch (requestError) {
      setError(requestError.message || 'No fue posible generar el reporte.');
    } finally {
      setLoading(false);
    }
  }, [filters, type, user]);

  useEffect(() => {
    reportService.getOptions(user)
      .then((response) => setOptions(response.data))
      .catch((requestError) => setError(requestError.message));
  }, [user]);
  useEffect(() => {
    if (activeCampaign?.id) {
      setFilters((current) => current.campaignId
        ? current : { ...current, campaignId: activeCampaign.id });
    }
  }, [activeCampaign]);
  useEffect(() => { loadReport(); }, [loadReport]);

  const leaders = useMemo(() => options.leaders.filter((leader) =>
    !filters.coordinatorId || String(leader.coordinatorId) === String(filters.coordinatorId)
  ), [filters.coordinatorId, options.leaders]);
  const tableColumns = useMemo(() => (report?.columns || []).map((column) => ({
    key: column.key, header: column.label,
  })), [report]);
  const setFilter = (key, value) => setFilters((current) => ({
    ...current, [key]: value, ...(key === 'coordinatorId' ? { leaderId: '' } : {}),
  }));
  const exportData = async () => {
    setError('');
    try {
      await reportService.exportExcel(type, filters);
      setNotice('Archivo Excel generado con tu alcance y filtros actuales.');
    } catch (exportError) {
      setError(exportError.message || 'No fue posible exportar el reporte.');
    }
  };
  const print = () => {
    try { openPrintView(report); } catch (printError) { setError(printError.message); }
  };
  const preparePdf = async () => {
    setError('');
    try {
      await reportService.exportPdf(type, filters, slug(report.title));
      setNotice('PDF generado correctamente con la grilla del reporte.');
    } catch (exportError) {
      setError(exportError.message || 'No fue posible generar el PDF.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-primary-600">Análisis y exportación</p>
          <h1 className="mt-1 text-2xl font-bold text-content-primary">Reportes</h1>
          <p className="mt-1 text-sm text-content-muted">Información consolidada según tu alcance jerárquico.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportData} disabled={!report || loading}><Download className="size-4" />Generar Excel</Button>
          <Button variant="outline" onClick={print} disabled={!report || loading}><Printer className="size-4" />Imprimir</Button>
          <Button variant="outline" onClick={preparePdf} disabled={!report || loading}><FileText className="size-4" />Descargar PDF</Button>
        </div>
      </div>

      {error && <Alert variant="danger" title="Error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert variant="success" title="Operación completada" onClose={() => setNotice('')}>{notice}</Alert>}

      <Card title="Configuración del reporte" description="Los filtros se aplican también a la tabla, el gráfico y las exportaciones.">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Select id="report-type" label="Reporte" value={type} onChange={(event) => setType(event.target.value)}
            options={Object.entries(REPORT_TYPE_LABELS).map(([value, label]) => ({ value, label }))} />
          <Select id="report-campaign" label="Campaña" value={filters.campaignId} onChange={(event) => setFilter('campaignId', event.target.value)}
            placeholder="Todas" options={options.campaigns.map((item) => ({ value: item.id, label: item.name }))} />
          <Select id="report-general-administrator" label="Administrador general" value={filters.generalAdministratorId} onChange={(event) => setFilter('generalAdministratorId', event.target.value)}
            placeholder="Todos" options={options.generalAdministrators.map((item) => ({ value: item.id, label: item.name }))} />
          <Select id="report-coordinator" label="Coordinador" value={filters.coordinatorId} onChange={(event) => setFilter('coordinatorId', event.target.value)}
            placeholder="Todos" options={options.coordinators.map((item) => ({ value: item.id, label: item.name }))} />
          <Select id="report-leader" label="Puntero" value={filters.leaderId} onChange={(event) => setFilter('leaderId', event.target.value)}
            placeholder="Todos" options={leaders.map((item) => ({ value: item.id, label: item.name }))} />
          <Select id="report-sectional" label="Seccional" value={filters.sectional} onChange={(event) => setFilter('sectional', event.target.value)}
            placeholder="Todas" options={options.sectionals.map((item) => ({ value: item.id, label: item.name }))} />
          <Select id="report-classification" label="Clasificación" value={filters.classification} onChange={(event) => setFilter('classification', event.target.value)}
            placeholder="Todas" options={Object.entries(VOTER_CLASSIFICATION_META).map(([value, item]) => ({ value, label: item.label }))} />
          <Select id="report-voter-status" label="Estado de votante" value={filters.voterStatus} onChange={(event) => setFilter('voterStatus', event.target.value)}
            placeholder="Todos" options={[{ value: 'confirmado', label: 'Confirmado' }, { value: 'pendiente', label: 'Pendiente' }, { value: 'inactivo', label: 'Inactivo' }]} />
          <Select id="report-sheet-status" label="Estado de planilla" value={filters.sheetStatus} onChange={(event) => setFilter('sheetStatus', event.target.value)}
            placeholder="Todos" options={Object.entries(SHEET_STATUS_META).map(([value, item]) => ({ value, label: item.label }))} />
          <div className="grid grid-cols-2 gap-3">
            <Input id="report-from" type="date" label="Desde" value={filters.fromDate} onChange={(event) => setFilter('fromDate', event.target.value)} />
            <Input id="report-to" type="date" label="Hasta" value={filters.toDate} onChange={(event) => setFilter('toDate', event.target.value)} />
          </div>
          <Input id="report-zone" label="Zona" value={filters.zone} onChange={(event) => setFilter('zone', event.target.value)} placeholder="Todas las zonas" />
          <Input id="report-search" label="Buscar" value={filters.search} onChange={(event) => setFilter('search', event.target.value)} placeholder="Cédula, nombre o teléfono" />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button onClick={loadReport} loading={loading}><Filter className="size-4" />Generar reporte</Button>
          <Button variant="ghost" onClick={() => setFilters({ ...initialFilters, campaignId: activeCampaign?.id || '' })}>
            <RefreshCw className="size-4" />Restablecer
          </Button>
        </div>
      </Card>

      {loading ? (
        <div className="grid min-h-64 place-items-center"><LoadingSpinner label="Generando reporte..." size="lg" /></div>
      ) : report && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <Card><div className="flex items-center gap-4"><span className="grid size-11 place-items-center rounded-control bg-primary-50 text-primary-700"><BarChart3 className="size-5" /></span><div><p className="text-sm text-content-muted">Filas del reporte</p><p className="text-2xl font-bold">{report.total}</p></div></div></Card>
            <Card><p className="text-sm text-content-muted">Última generación</p><p className="mt-2 font-semibold">{new Date(report.generatedAt).toLocaleString('es-PY')}</p></Card>
          </div>
          <Card title={report.title} description="Representación gráfica de los datos filtrados."><ReportChart report={report} /></Card>
          <Card title="Detalle" description={`${report.rows.length} filas disponibles para consulta y exportación.`}>
            <Table columns={tableColumns} data={report.rows} rowKey="id" emptyMessage="No hay datos para los filtros seleccionados." />
          </Card>
        </>
      )}
    </div>
  );
}

export default ReportsPage;
